/**
 * @file components/chat/AgentStatusBubble.tsx
 * CHAT-06b — Bulle de statut animée émise pendant le streaming agent.
 *
 * Affichée dès la réception du premier événement SSE "status" (intent < 200ms)
 * et disparaît automatiquement à la réception du premier token LLM.
 * Gère la progression graduelle : intent → memory → rag → generating.
 */
import React from 'react';
import { Bot, Loader2 } from 'lucide-react';

interface AgentStatusBubbleProps {
  /** Texte de statut courant (localisé, mis à jour à chaque événement SSE "status"). */
  text: string;
  /** Si true, la bulle est visible. Doit être false dès le premier token. */
  visible: boolean;
}

/**
 * Bulle de statut animée affichée pendant le traitement de l'agent.
 * Disparaît au premier token (contrôlée par `visible`).
 */
export default function AgentStatusBubble({
  text,
  visible,
}: AgentStatusBubbleProps): React.ReactElement | null {
  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`Agent : ${text}`}
      className="flex items-end gap-2"
    >
      {/* Avatar agent — identique à MessageBubble */}
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Bot size={15} />
      </div>

      {/* Bulle de statut */}
      <div className="flex max-w-[78%] items-center gap-2 rounded-2xl rounded-bl-sm border border-border bg-card px-4 py-2.5 shadow-sm">
        <Loader2
          size={13}
          className="shrink-0 animate-spin text-muted-foreground"
          aria-hidden
        />
        <span className="text-sm text-muted-foreground">{text}</span>
      </div>
    </div>
  );
}
